/**
 * Ракурсы опорного кадра (видео-ролик из готового кадра, §7.1/§11 спеки
 * `2026-10-13-frame-to-video-design.md`): код → подпись, порядок — порядок подачи кадров проекта
 * (`generate.VIEW_ANGLES`): код → подпись, память выбранной модели и правило «что можно убрать».
 *
 * Гейт поля «длительность куска» жил здесь, пока у создания было окно параметров; с 15.10 (задача Оли
 * №305) кнопка «Сделать видео» заводит проект сразу на умолчаниях СЕРВЕРА, и проверять на фронте
 * стало нечего — границы куска держит `videoscenario._shot_ms`, длительности правятся на дорожке.
 *
 * Без импортов из `@/…`: модуль проверяется `node --test` (tests/videoAngles.test.ts) как есть.
 */

export type AngleCode = "close" | "medium" | "full" | "back" | "motion";

/**
 * Можно ли убрать ракурс из проекта до генерации (задача Оли №263, ручка `views/plan`).
 *
 * Ракурс, который занял ИСХОДНЫЙ кадр проекта (`source`), тоже не убирается галочкой состава: его
 * снимают списком в строке «Исходный кадр» (задача Оли №305) — сам кадр при этом остаётся в проекте,
 * а сервер на попытку убрать его составом отвечает 409 `VIEWS_PAID`.
 *
 * Нельзя, когда ракурс уже стоил денег: есть стилл или хотя бы одна генерация в ЛЮБОМ статусе —
 * `queued` оплатит воркер, а у `failed` деньги могли уже уйти. Такой ракурс убирают удалением кадра
 * на дорожке, как любой кадр проекта. Ракурса нет в проекте (`undefined`) — убирать нечего.
 */
export function canDropView(
  row:
    | { still: { variant_id: number; asset_path: string } | null; gens: { status: string }[]; source?: boolean }
    | undefined,
): boolean {
  if (!row) return false;
  if (row.source) return false;
  return row.still === null && row.gens.length === 0;
}

/**
 * Код → подпись, в порядке подачи кадров (`generate.VIEW_ANGLES`). `hidden` — ракурс не предлагается
 * (задача Оли №315: «Движение можно пока вообще убрать»), но остаётся в каталоге: там, где его кадр
 * в проекте уже стоит, строка обязана показываться — иначе кадр не убрать и не перегенерировать.
 * Подпись «Крупный · детали» — потому что задание этого ракурса стало макро лифа без лица (№315).
 */
export const ANGLES: { code: AngleCode; label: string; hidden?: boolean }[] = [
  { code: "close", label: "Крупный · детали" },
  { code: "medium", label: "Средний" },
  { code: "full", label: "В полный рост" },
  { code: "back", label: "Спина" },
  { code: "motion", label: "Движение", hidden: true },
];

/**
 * Какие строки показывать в панели «Ракурсы» этого проекта: каталог без скрытых ПЛЮС те скрытые,
 * у которых в проекте есть кадр. Порядок — каталожный (он же порядок кадров на дорожке).
 */
export function anglesFor(
  shots: readonly { framing: string | null }[],
): { code: AngleCode; label: string; hidden?: boolean }[] {
  const present = new Set(shots.map((s) => s.framing));
  return ANGLES.filter((a) => !a.hidden || present.has(a.code));
}

export const ANGLE_LABEL: Record<AngleCode, string> = Object.fromEntries(
  ANGLES.map((a) => [a.code, a.label]),
) as Record<AngleCode, string>;

export const ANGLE_CODES: AngleCode[] = ANGLES.map((a) => a.code);

/**
 * Модель ракурсов запоминается в этом браузере: панель «Ракурсы» подставляет её при открытии.
 * Жила в окне «Сделать видео», которое убрано вместе с попапом создания (задача Оли №305).
 */
export const ANGLE_MODEL_KEY = "reels.angles.model";
